import {EventDateFormValuesType, EventFormValuesType} from "utils/form.ts";
import {useTranslation} from "react-i18next";
import {useFormikContext} from "formik";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import dayjs from "dayjs";
import {enCA, frCA} from "date-fns/locale";
import useOutsideClick from "utils/effects/useOutsideClick.ts";
import useKeyUp from "utils/effects/useKeyUp.ts";
import FormLabel from "components/form/FormLabel.tsx";
import FormInput from "components/form/FormInput.tsx";
import {DayPicker} from "react-day-picker";
import {HOURS_SELECT, MINUTES_SELECT} from "assets/data/time.ts";
import FormSelect from "components/form/FormSelect.tsx";
import {usePrevious} from "react-use";
import {useAppSelector} from "config/store";
import {LANGS} from "globals/constants.ts";

function EventDateItem(props: {
    date: EventDateFormValuesType,
    idx: number,
    children?: React.ReactNode,
    disabled?: boolean
    onChanged?: () => void
}) {
    const {idx, disabled, onChanged} = props
    const {t} = useTranslation()
    const {values, setFieldValue, errors} = useFormikContext<EventFormValuesType>()
    const currentLang = useAppSelector(state => state.global.currentLang)
    const dateValues = values.dates[idx]

    const previousDateValues = usePrevious(dateValues)

    const dateRef = useRef<{ startDate: HTMLDivElement | null, endDate: HTMLDivElement | null }>({
        startDate: null,
        endDate: null
    });

    const [showDropdownOptions, setShowDropdownOptions] = useState<{ startDate: boolean, endDate: boolean }>({
        startDate: false,
        endDate: false
    })


    const onDayChange = useCallback((name: 'startDate' | 'endDate') => {
        return (date: Date) => {
            if (disabled) return
            const currentTime = dateValues[name] ? dayjs(dateValues[name]).format('HH:mm') : '00:00'
            const day = dayjs(date).format('YYYY-MM-DD')

            let finalDateDayJs = dayjs(`${day} ${currentTime}`, 'YYYY-MM-DD HH:mm')

            if (name === 'endDate') {
                if (dateValues.startDate) {
                    const startDateDay = dayjs(dateValues.startDate)

                    //end date cannot be before start date
                    if (finalDateDayJs.isBefore(startDateDay, 'day')) {
                        return;
                    }
                    //if same day wanted
                    if (finalDateDayJs.isSame(startDateDay, 'day')) {
                        //if end date will be before start date, set it to start date + 15 min
                        if (finalDateDayJs.isBefore(startDateDay, 'minutes')) {
                            finalDateDayJs = startDateDay.clone().add(15, 'minutes')
                        }
                    }
                }
            }

            //dont include timezone in formatted value
            setFieldValue(`dates[${idx}][${name}]`, finalDateDayJs.format('YYYY-MM-DD HH:mm:ss'))

            setShowDropdownOptions(prev => ({
                ...prev,
                [name]: false
            }))


            if (name === 'startDate' && dateValues.endDate) {
                //const endDateTime = dateValues.endDate ? dayjs(dateValues.endDate).format('HH:mm:ss') : null//keep time
                const newEndDate = finalDateDayJs.clone().add(15, 'minutes').format('YYYY-MM-DD HH:mm:ss')//make it 15 min event long
                //if end date is before start date, set it to start date
                if (dayjs(dateValues.endDate).isBefore(day, 'day')) {
                    setFieldValue(`dates[${idx}][endDate]`, newEndDate)
                }

                if (dayjs(dateValues.endDate).diff(day, 'day', true) > 1) {
                    setFieldValue(`dates[${idx}][endDate]`, newEndDate)
                }
            }

            if (name === 'endDate' && dateValues.startDate) {
                //  const startDateTime = dateValues.startDate ? dayjs(dateValues.startDate).format('HH:mm:ss') : null//keep time
                const newStartDate = finalDateDayJs.clone().subtract(15, 'minutes').format('YYYY-MM-DD HH:mm:ss')

                //if end date is before start date, set it to start date
                if (dayjs(dateValues.startDate).isAfter(day, 'day')) {
                    setFieldValue(`dates[${idx}][startDate]`, newStartDate)
                }
            }
        }
    }, [dateValues, disabled, idx, setFieldValue])

    const onHourChange = useCallback((name: 'startDate' | 'endDate') => {
        return (e: { target: { value: string } }) => {
            if (disabled) return
            if (!dateValues[name]) return
            const dayJsDate = dayjs(dateValues[name])
            const currentDay = dayJsDate.format('YYYY-MM-DD')
            const minutes = dayJsDate.format('mm')
            const hour = e.target.value

            const finalDateDay = dayjs(`${currentDay} ${hour}:${minutes}`, 'YYYY-MM-DD HH:mm')

            //dont include timezone in formatted value
            setFieldValue(`dates[${idx}][${name}]`, finalDateDay.format('YYYY-MM-DD HH:mm:ss'))

            if (name === 'startDate' && dateValues.endDate) {
                //const endDateTime = dateValues.endDate ? dayjs(dateValues.endDate).format('HH:mm:ss') : null//keep time
                const newEndDate = finalDateDay.clone().add(15, 'minutes').format('YYYY-MM-DD HH:mm:ss')//make it 15 min event long
                ///make sure if we change time of start date, the end date is always after
                if (dayjs(dateValues.endDate).isBefore(finalDateDay, 'minutes')) {
                    setFieldValue(`dates[${idx}][endDate]`, newEndDate)
                }
            }

            if (name === 'endDate' && dateValues.startDate) {
                const isSameHour = dayjs(dateValues.startDate).format('HH') === finalDateDay.format('HH')
                //const endDateTime = dateValues.endDate ? dayjs(dateValues.endDate).format('HH:mm:ss') : null//keep time
                //make sure we got atleast 15 min event
                ///make sure if we change time of start date, the end date is always after
                if (isSameHour && finalDateDay.isBefore(dayjs(dateValues.startDate), 'minutes')) {
                    setFieldValue(`dates[${idx}][endDate]`, dayjs(dateValues.startDate).clone().add(15, 'minutes').format('YYYY-MM-DD HH:mm:ss'))
                }
                //if by changing the time, we go over 1 day, set it back to 1 day
                if (finalDateDay.diff(dayjs(dateValues.startDate), 'day', true) > 1) {
                    setFieldValue(`dates[${idx}][endDate]`, dayjs(dateValues.startDate).clone().add(1, 'day').format('YYYY-MM-DD HH:mm:ss'))
                }
            }
        }
    }, [disabled, idx, setFieldValue, dateValues])

    const onMinuteChange = useCallback((name: 'startDate' | 'endDate') => {
        return (e: { target: { value: string } }) => {
            if (disabled) return
            if (!dateValues[name]) return
            const dayJsDate = dayjs(dateValues[name])
            const currentDay = dayJsDate.format('YYYY-MM-DD')
            const hour = dayJsDate.format('HH')
            const minutes = e.target.value
            const finalDate = dayjs(`${currentDay} ${hour}:${minutes}`, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm:ss')

            //dont include timezone in formatted value
            setFieldValue(`dates[${idx}][${name}]`, finalDate)
        }
    }, [disabled, idx, setFieldValue, dateValues])

    const currentDayPickerLocale = useMemo(() => {
        return currentLang === LANGS.FR ? frCA : enCA
    }, [currentLang])

    useOutsideClick(dateRef.current.startDate, () => {
        setShowDropdownOptions(prev => ({
            ...prev,
            startDate: false
        }))
    })

    useOutsideClick(dateRef.current.endDate, () => {
        setShowDropdownOptions(prev => ({
            ...prev,
            endDate: false
        }))
    })

    useKeyUp('Escape', () => {
        setShowDropdownOptions({
            endDate: false,
            startDate: false
        })
    })


    const dateOptions = useMemo(() => {
        let endDateHours = HOURS_SELECT
        let endDateMinutes = MINUTES_SELECT
        let endDisabledDays: any[] = []
        const startDisabledDays: any[] = [{before: dayjs().startOf('day').toDate()}]
        if (dateValues.startDate) {
            const startDateDay = dayjs(dateValues.startDate)
            const isSameDay = dateValues.endDate ? dayjs(dateValues.endDate).isSame(startDateDay, 'day') : false
            const isSameHour = dateValues.endDate ? dayjs(dateValues.endDate).format('HH') === startDateDay.format('HH') : false
            const startDateHour = parseInt(startDateDay.format('HH'))
            const startDateMinute = parseInt(startDateDay.format('mm'))

            endDisabledDays = [{
                before: startDateDay.startOf('day').toDate(),
                after: startDateDay.endOf('day').add(1, 'day').toDate()
            }]

            endDateHours = HOURS_SELECT.filter(h => {
                if (isSameDay) {
                    return startDateHour <= parseInt(h.value)
                }
                return startDateHour >= parseInt(h.value)
            })

            endDateMinutes = MINUTES_SELECT.filter(m => {
                //dont include the current minute, so 15 min minimum
                if (isSameDay) {
                    if (isSameHour) {
                        if (startDateMinute === 45) {
                            return startDateMinute === parseInt(m.value)
                        }
                        return startDateMinute < parseInt(m.value)
                    }
                    return true
                }

                if (isSameHour) {
                    return startDateMinute >= parseInt(m.value)
                }
                return true
            })
        }
        return {
            startDate: {
                hours: HOURS_SELECT,
                minutes: MINUTES_SELECT,
                disabledDays: startDisabledDays,
                initialMonth: dateValues.startDate ? dayjs(dateValues.startDate).toDate() : undefined
            },
            endDate: {
                hours: endDateHours,
                minutes: endDateMinutes,
                disabledDays: endDisabledDays,
                initialMonth: dateValues.endDate ? dayjs(dateValues.endDate).toDate() : undefined
            }
        }
    }, [dateValues])

    const dateStrValues = useMemo(() => {
        const finalData: {
            startDate: { time: string; hour: string; minute: string; day: string, dayDate: Date | undefined },
            endDate: { time: string; hour: string; minute: string; day: string, dayDate: Date | undefined }
        } = {
            startDate: {time: '00:00', hour: '00', minute: '00', day: '', dayDate: undefined},
            endDate: {time: '00:00', hour: '00', minute: '00', day: '', dayDate: undefined},
        };

        (['startDate', 'endDate'] as const).map((dateStr) => {
            const data = dateValues[dateStr]
            const hour = data ? dayjs(data).format('HH') : '00';
            const minute = data ? dayjs(data).format('mm') : '00';
            const day = data ? dayjs(data).format('LL') : '';
            const dayDate = data ? dayjs(data).toDate() : undefined
            finalData[dateStr] = {
                time: `${hour}:${minute}`,
                hour: hour,
                minute: minute,
                day: day,
                dayDate: dayDate
            }
        })
        return finalData
    }, [dateValues])

    const dateErrors = useMemo(() => {
        return errors.dates && errors.dates[idx] ? {
            startDate: errors.dates[idx].startDate,
            endDate: errors.dates[idx].endDate
        } : {
            startDate: '',
            endDate: ''
        }
    }, [idx, errors])

    useEffect(() => {
        if (!previousDateValues) return
        if (!dateValues) return
        if (!onChanged) return

        if (previousDateValues.startDate !== dateValues.startDate || previousDateValues.endDate !== dateValues.endDate) {
            onChanged()
        }

        //dont include onChanged in deps, it will cause infinite loop
    }, [previousDateValues, dateValues, onChanged])


    return (
        <div
            className="flex flex-row flex-wrap items-center justify-start gap-4"
            key={idx}>
            {(['startDate', 'endDate'] as const).map((dateStr) => {
                return (
                    <div
                        className="relative w-full md:w-1/3"
                        key={dateStr}>
                        <FormLabel htmlFor={`${dateStr}Day`}>
                            {t(`forms.${dateStr}`)}
                        </FormLabel>
                        <div
                            className={`input flex w-full flex-row items-center justify-center gap-2 ${disabled ? '-disabled' : ''}`}>

                            <div className="relative" ref={(ref => dateRef.current[dateStr] = ref)}>

                                <FormInput id={`${dateStr}Day`}
                                           type="text"
                                           noClassName
                                           disabled={disabled}
                                           onClick={() => {
                                               setShowDropdownOptions(prev => ({
                                                   ...prev,
                                                   [dateStr]: true
                                               }))
                                           }}
                                           onChange={() => {
                                           }}
                                           formikField={false}
                                           value={dateStrValues[dateStr].day}
                                />
                                <div
                                    className={`absolute left-0  top-full z-30 rounded ${showDropdownOptions[dateStr] ? 'block' : 'hidden'}`}>
                                    <DayPicker locale={currentDayPickerLocale}
                                               mode="single"
                                               defaultMonth={dateOptions[dateStr].initialMonth}
                                               disabled={dateOptions[dateStr].disabledDays}
                                               selected={dateStrValues[dateStr].dayDate}
                                               onDayClick={onDayChange(dateStr)}/>
                                </div>
                            </div>
                            <span>/</span>

                            <FormSelect
                                formikField={false}
                                name={`${dateStr}Hour`}
                                noClassName
                                disabled={disabled}
                                value={dateStrValues[dateStr].hour}
                                onChange={onHourChange(dateStr)}>
                                {dateOptions[dateStr].hours.map(item => (
                                    <option key={item.value} value={item.value}>
                                        {item.label}h
                                    </option>
                                ))}
                            </FormSelect>
                            <span>:</span>
                            <FormSelect
                                formikField={false}
                                name={`${dateStr}Minute`}
                                noClassName
                                disabled={disabled}
                                value={dateStrValues[dateStr].minute}
                                onChange={onMinuteChange(dateStr)}>
                                {dateOptions[dateStr].minutes.map(item => (
                                    <option key={item.value} value={item.value}>{item.label}</option>
                                ))}
                            </FormSelect>
                        </div>
                        {dateErrors[dateStr] && (
                            <p className="error">{dateErrors[dateStr]}</p>
                        )}
                    </div>
                )
            })}
            {props.children}
        </div>
    )
}

export default EventDateItem
